import React, { useState, useEffect } from 'react';
import { Thermometer, Droplets, Wind, CloudRain, Home, Gauge, Activity } from 'lucide-react';
import { Device } from '../../types';
import styled from 'styled-components';

interface NetatmoStationDetailsProps {
  mainStation: Device;
  modules: Device[];
}

const StationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
`;

const ModuleCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #3a3a3a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #2a2a2a;
`;

const ModuleIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #1d388d 0%, #2a4ab8 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const ModuleInfo = styled.div`
  flex: 1;
`;

const ModuleName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px 0;
`;

const ModuleType = styled.div`
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModuleStatus = styled.div<{ status: 'online' | 'offline' | 'warning' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => 
    props.status === 'online' ? '#10b981' : 
    props.status === 'warning' ? '#f59e0b' : '#ef4444'};
`;

const SensorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
`;

const SensorItem = styled.div`
  background: #0f0f0f;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  padding: 16px;
`;

const SensorLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SensorValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
`;

const SensorUnit = styled.span`
  font-size: 14px;
  color: #666;
  margin-left: 4px;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 14px;
`;

const LastUpdate = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #2a2a2a;
  text-align: center;
`;

const NetatmoStationDetails: React.FC<NetatmoStationDetailsProps> = ({ mainStation, modules }) => {
  const [latestData, setLatestData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllLatestData = async () => {
      try {
        // Fetch latest data for main station
        const mainResponse = await fetch(`/api/devices/${mainStation.id}/latest`);
        const dataMap: Record<string, any> = {};

        if (mainResponse.ok) {
          const mainData = await mainResponse.json();
          console.log('Main station raw response:', mainData);
          // The API returns { timestamp, data: decoded_json }
          // We want to extract just the decoded_json content
          const extractedData = mainData.data || mainData;
          dataMap[mainStation.id.toString()] = extractedData;
          console.log('Main station processed data:', dataMap[mainStation.id.toString()]);
        } else {
          console.error('Main station fetch failed:', mainResponse.status, await mainResponse.text());
        }

        // Fetch latest data for all modules
        await Promise.all(
          modules.map(async (module) => {
            try {
              const response = await fetch(`/api/devices/${module.id}/latest`);
              if (response.ok) {
                const moduleData = await response.json();
                console.log(`Module ${module.id} (${module.name}) raw response:`, moduleData);
                // Extract the decoded_json content from the nested 'data' property
                const extractedData = moduleData.data || moduleData;
                dataMap[module.id.toString()] = extractedData;
                console.log(`Module ${module.id} processed data:`, dataMap[module.id.toString()]);
              } else {
                console.error(`Module ${module.id} fetch failed:`, response.status, await response.text());
              }
            } catch (err) {
              console.error(`Failed to fetch data for module ${module.id}`, err);
            }
          })
        );

        console.log('Final dataMap:', dataMap);
        setLatestData(dataMap);
      } catch (err) {
        console.error('Error fetching latest data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllLatestData();
  }, [mainStation.id, modules]);

  const getModuleIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'netatmo_weather':
        return <Home size={24} />;
      case 'netatmo_outdoor':
        return <Thermometer size={24} />;
      case 'netatmo_rain':
        return <CloudRain size={24} />;
      case 'netatmo_wind':
        return <Wind size={24} />;
      default:
        return <Activity size={24} />;
    }
  };

  const getModuleTypeName = (deviceType: string) => {
    switch (deviceType) {
      case 'netatmo_weather':
        return 'Indoor Station';
      case 'netatmo_outdoor':
        return 'Outdoor Module';
      case 'netatmo_rain':
        return 'Rain Gauge';
      case 'netatmo_wind':
        return 'Wind Gauge';
      default:
        return 'Module';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return date.toLocaleString();
  };

  const renderMainStationSensors = (data: any) => {
    if (!data) {
      console.log('renderMainStationSensors: No data provided');
      return <NoDataMessage>No data available</NoDataMessage>;
    }

    console.log('renderMainStationSensors: Received data:', data);

    // Check if any sensor field exists (including 0 values)
    const hasAnyData =
      (data.temperature !== undefined && data.temperature !== null) ||
      (data.humidity !== undefined && data.humidity !== null) ||
      (data.co2 !== undefined && data.co2 !== null) ||
      (data.noise !== undefined && data.noise !== null) ||
      (data.pressure !== undefined && data.pressure !== null);

    if (!hasAnyData) {
      return (
        <NoDataMessage>
          No sensor data available
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#444' }}>
            Available keys: {Object.keys(data).join(', ')}
          </div>
        </NoDataMessage>
      );
    }

    return (
      <SensorGrid>
        {(data.temperature !== undefined && data.temperature !== null) && (
          <SensorItem>
            <SensorLabel>
              <Thermometer size={14} />
              Temperature
            </SensorLabel>
            <SensorValue>
              {typeof data.temperature === 'number' ? data.temperature.toFixed(1) : data.temperature}
              <SensorUnit>°C</SensorUnit>
            </SensorValue>
            {data.temp_trend && (
              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                Trend: {data.temp_trend}
              </div>
            )}
          </SensorItem>
        )}
        {(data.min_temp !== undefined && data.min_temp !== null) && (
          <SensorItem>
            <SensorLabel>
              <Thermometer size={14} />
              Min Temperature
            </SensorLabel>
            <SensorValue>
              {typeof data.min_temp === 'number' ? data.min_temp.toFixed(1) : data.min_temp}
              <SensorUnit>°C</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {(data.max_temp !== undefined && data.max_temp !== null) && (
          <SensorItem>
            <SensorLabel>
              <Thermometer size={14} />
              Max Temperature
            </SensorLabel>
            <SensorValue>
              {typeof data.max_temp === 'number' ? data.max_temp.toFixed(1) : data.max_temp}
              <SensorUnit>°C</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {(data.humidity !== undefined && data.humidity !== null) && (
          <SensorItem>
            <SensorLabel>
              <Droplets size={14} />
              Humidity
            </SensorLabel>
            <SensorValue>
              {data.humidity}
              <SensorUnit>%</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {(data.co2 !== undefined && data.co2 !== null) && (
          <SensorItem>
            <SensorLabel>
              <Activity size={14} />
              CO₂
            </SensorLabel>
            <SensorValue style={{
              color: data.co2 < 1000 ? '#10b981' : data.co2 < 1500 ? '#f59e0b' : '#ef4444'
            }}>
              {data.co2}
              <SensorUnit>ppm</SensorUnit>
            </SensorValue>
            <div style={{
              fontSize: '11px',
              marginTop: '4px',
              color: data.co2 < 1000 ? '#10b981' : data.co2 < 1500 ? '#f59e0b' : '#ef4444'
            }}>
              {data.co2 < 1000 ? 'Good' : data.co2 < 1500 ? 'Fair' : 'Poor'}
            </div>
          </SensorItem>
        )}
        {(data.noise !== undefined && data.noise !== null) && (
          <SensorItem>
            <SensorLabel>
              <Activity size={14} />
              Noise
            </SensorLabel>
            <SensorValue>
              {data.noise}
              <SensorUnit>dB</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {(data.pressure !== undefined && data.pressure !== null) && (
          <SensorItem>
            <SensorLabel>
              <Gauge size={14} />
              Pressure
            </SensorLabel>
            <SensorValue>
              {typeof data.pressure === 'number' ? data.pressure.toFixed(1) : data.pressure}
              <SensorUnit>mbar</SensorUnit>
            </SensorValue>
            {data.pressure_trend && (
              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                Trend: {data.pressure_trend}
              </div>
            )}
          </SensorItem>
        )}
        {(data.absolute_pressure !== undefined && data.absolute_pressure !== null && data.absolute_pressure !== data.pressure) && (
          <SensorItem>
            <SensorLabel>
              <Gauge size={14} />
              Absolute Pressure
            </SensorLabel>
            <SensorValue>
              {typeof data.absolute_pressure === 'number' ? data.absolute_pressure.toFixed(1) : data.absolute_pressure}
              <SensorUnit>mbar</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
      </SensorGrid>
    );
  };

  const renderOutdoorSensors = (data: any) => {
    if (!data) return <NoDataMessage>No data available</NoDataMessage>;

    return (
      <SensorGrid>
        {data.temperature !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Thermometer size={14} />
              Temperature
            </SensorLabel>
            <SensorValue>
              {data.temperature.toFixed(1)}
              <SensorUnit>°C</SensorUnit>
            </SensorValue>
            {data.temp_trend && (
              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                Trend: {data.temp_trend}
              </div>
            )}
          </SensorItem>
        )}
        {data.min_temp !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Thermometer size={14} />
              Min Temperature
            </SensorLabel>
            <SensorValue>
              {data.min_temp.toFixed(1)}
              <SensorUnit>°C</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.max_temp !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Thermometer size={14} />
              Max Temperature
            </SensorLabel>
            <SensorValue>
              {data.max_temp.toFixed(1)}
              <SensorUnit>°C</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.humidity !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Droplets size={14} />
              Humidity
            </SensorLabel>
            <SensorValue>
              {data.humidity}
              <SensorUnit>%</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.battery_percent !== undefined && (
          <SensorItem>
            <SensorLabel>
              Battery
            </SensorLabel>
            <SensorValue style={{
              color: data.battery_percent > 80 ? '#10b981' : data.battery_percent > 20 ? '#f59e0b' : '#ef4444'
            }}>
              {data.battery_percent}
              <SensorUnit>%</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.rf_status !== undefined && (
          <SensorItem>
            <SensorLabel>
              Signal Quality
            </SensorLabel>
            <SensorValue style={{ fontSize: '14px' }}>
              {data.rf_status > 80 ? 'Excellent' : data.rf_status > 60 ? 'Good' : data.rf_status > 40 ? 'Fair' : 'Weak'}
            </SensorValue>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              RF: {data.rf_status}
            </div>
          </SensorItem>
        )}
        {data.last_seen && (
          <SensorItem>
            <SensorLabel>
              Last Seen
            </SensorLabel>
            <SensorValue style={{ fontSize: '12px' }}>
              {formatTimestamp(data.last_seen)}
            </SensorValue>
          </SensorItem>
        )}
      </SensorGrid>
    );
  };

  const renderRainSensors = (data: any) => {
    if (!data) return <NoDataMessage>No data available</NoDataMessage>;

    return (
      <SensorGrid>
        {data.rain !== undefined && (
          <SensorItem>
            <SensorLabel>
              <CloudRain size={14} />
              Rain Rate
            </SensorLabel>
            <SensorValue>
              {data.rain.toFixed(2)}
              <SensorUnit>mm/h</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.rain_1h !== undefined && (
          <SensorItem>
            <SensorLabel>
              <CloudRain size={14} />
              Last Hour
            </SensorLabel>
            <SensorValue>
              {data.rain_1h.toFixed(2)}
              <SensorUnit>mm</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.rain_24h !== undefined && (
          <SensorItem>
            <SensorLabel>
              <CloudRain size={14} />
              Last 24 Hours
            </SensorLabel>
            <SensorValue>
              {data.rain_24h.toFixed(2)}
              <SensorUnit>mm</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.battery_percent !== undefined && (
          <SensorItem>
            <SensorLabel>
              Battery
            </SensorLabel>
            <SensorValue style={{
              color: data.battery_percent > 80 ? '#10b981' : data.battery_percent > 20 ? '#f59e0b' : '#ef4444'
            }}>
              {data.battery_percent}
              <SensorUnit>%</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.rf_status !== undefined && (
          <SensorItem>
            <SensorLabel>
              Signal Quality
            </SensorLabel>
            <SensorValue style={{ fontSize: '14px' }}>
              {data.rf_status > 80 ? 'Excellent' : data.rf_status > 60 ? 'Good' : data.rf_status > 40 ? 'Fair' : 'Weak'}
            </SensorValue>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              RF: {data.rf_status}
            </div>
          </SensorItem>
        )}
        {data.last_seen && (
          <SensorItem>
            <SensorLabel>
              Last Seen
            </SensorLabel>
            <SensorValue style={{ fontSize: '12px' }}>
              {formatTimestamp(data.last_seen)}
            </SensorValue>
          </SensorItem>
        )}
      </SensorGrid>
    );
  };

  const renderWindSensors = (data: any) => {
    if (!data) return <NoDataMessage>No data available</NoDataMessage>;

    const getWindDirection = (angle: number) => {
      const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
      const index = Math.round(angle / 22.5) % 16;
      return directions[index];
    };

    return (
      <SensorGrid>
        {data.wind_strength !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Wind size={14} />
              Wind Speed
            </SensorLabel>
            <SensorValue>
              {data.wind_strength}
              <SensorUnit>km/h</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.wind_angle !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Wind size={14} />
              Wind Direction
            </SensorLabel>
            <SensorValue>
              {data.wind_angle}
              <SensorUnit>°</SensorUnit>
            </SensorValue>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              {getWindDirection(data.wind_angle)}
            </div>
          </SensorItem>
        )}
        {data.gust_strength !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Wind size={14} />
              Gust Speed
            </SensorLabel>
            <SensorValue style={{ color: data.gust_strength > 50 ? '#ef4444' : data.gust_strength > 30 ? '#f59e0b' : '#10b981' }}>
              {data.gust_strength}
              <SensorUnit>km/h</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.gust_angle !== undefined && (
          <SensorItem>
            <SensorLabel>
              <Wind size={14} />
              Gust Direction
            </SensorLabel>
            <SensorValue>
              {data.gust_angle}
              <SensorUnit>°</SensorUnit>
            </SensorValue>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              {getWindDirection(data.gust_angle)}
            </div>
          </SensorItem>
        )}
        {data.max_wind_angle !== undefined && (
          <SensorItem>
            <SensorLabel>
              Max Wind Direction
            </SensorLabel>
            <SensorValue>
              {data.max_wind_angle}
              <SensorUnit>°</SensorUnit>
            </SensorValue>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              {getWindDirection(data.max_wind_angle)}
            </div>
          </SensorItem>
        )}
        {data.max_wind_strength !== undefined && (
          <SensorItem>
            <SensorLabel>
              Max Wind Speed
            </SensorLabel>
            <SensorValue>
              {data.max_wind_strength}
              <SensorUnit>km/h</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.battery_percent !== undefined && (
          <SensorItem>
            <SensorLabel>
              Battery
            </SensorLabel>
            <SensorValue style={{
              color: data.battery_percent > 80 ? '#10b981' : data.battery_percent > 20 ? '#f59e0b' : '#ef4444'
            }}>
              {data.battery_percent}
              <SensorUnit>%</SensorUnit>
            </SensorValue>
          </SensorItem>
        )}
        {data.rf_status !== undefined && (
          <SensorItem>
            <SensorLabel>
              Signal Quality
            </SensorLabel>
            <SensorValue style={{ fontSize: '14px' }}>
              {data.rf_status > 80 ? 'Excellent' : data.rf_status > 60 ? 'Good' : data.rf_status > 40 ? 'Fair' : 'Weak'}
            </SensorValue>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              RF: {data.rf_status}
            </div>
          </SensorItem>
        )}
        {data.last_seen && (
          <SensorItem>
            <SensorLabel>
              Last Seen
            </SensorLabel>
            <SensorValue style={{ fontSize: '12px' }}>
              {formatTimestamp(data.last_seen)}
            </SensorValue>
          </SensorItem>
        )}
      </SensorGrid>
    );
  };

  const renderModuleSensors = (module: Device, data: any) => {
    switch (module.deviceType) {
      case 'netatmo_outdoor':
      case 'netatmo_indoor':
        return renderOutdoorSensors(data);
      case 'netatmo_rain':
        return renderRainSensors(data);
      case 'netatmo_wind':
        return renderWindSensors(data);
      default:
        return <NoDataMessage>No data available</NoDataMessage>;
    }
  };

  if (loading) {
    return (
      <StationContainer>
        <NoDataMessage>Loading station data...</NoDataMessage>
      </StationContainer>
    );
  }

  const allModules = [mainStation, ...modules];

  return (
    <StationContainer>
      <ModuleGrid>
        {allModules.map((module) => {
          const data = latestData[module.id.toString()];
          return (
            <ModuleCard key={module.id}>
              <ModuleHeader>
                <ModuleIcon>
                  {getModuleIcon(module.deviceType || 'netatmo_weather')}
                </ModuleIcon>
                <ModuleInfo>
                  <ModuleName>{module.name}</ModuleName>
                  <ModuleType>{getModuleTypeName(module.deviceType || 'netatmo_weather')}</ModuleType>
                </ModuleInfo>
                <ModuleStatus status={module.status} />
              </ModuleHeader>

              {module.deviceType === 'netatmo_weather'
                ? renderMainStationSensors(data)
                : renderModuleSensors(module, data)
              }

              {data && data.timestamp && (
                <LastUpdate>
                  Last updated: {formatTimestamp(data.timestamp)}
                </LastUpdate>
              )}
            </ModuleCard>
          );
        })}
      </ModuleGrid>
    </StationContainer>
  );
};

export default NetatmoStationDetails;
